import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../lib/useFocusTrap.js';
import { motion, token } from '../theme/tokens.js';
import { Checkbox } from './Checkbox.js';

export interface ConsentDialogProps {
  open: boolean;
  /** Fired only when the reader ticked the box *and* pressed confirm. */
  onAccept: () => void;
  /** Cancel, overlay click, or Escape. Never an acceptance. */
  onCancel: () => void;
  title: string;
  /**
   * The statement, rendered **exactly** as given — whitespace preserved, no
   * reflow, no trimming.
   *
   * This is the one prop in this package that must not be edited on its way to
   * the screen: it is the server's wording (NTF-4, USR-13), and an acceptance
   * recorded against text nobody can reproduce is not a record of anything.
   */
  statement: string;
  acceptLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  /** A save is in flight. Confirm is held; the dialog stays open. */
  busy?: boolean;
  /** A save failed. Shown in place, because closing would lose the attempt. */
  error?: string;
}

/**
 * A one-time consent gate: read a statement, tick a box, confirm.
 *
 * **A centred modal rather than a `DetailDrawer`.** A drawer is for detail
 * beside the thing you are looking at, and this is a decision that blocks the
 * act behind it — the design system names exactly this pattern (its "Affidavit
 * certification modal that gates report finalization"). The two share their
 * dialog *behaviour* through `useFocusTrap` and nothing else.
 *
 * **The gate is two parts and both are required.** Reading is not agreeing, so
 * confirm stays disabled until the box is ticked — `SignatureScreen`'s rule for
 * the same statement mechanism, and the reason a single "I agree" button would
 * be worse rather than simpler.
 *
 * **Dismissing is never accepting.** Escape, the overlay, and cancel all reach
 * `onCancel`; `onAccept` has exactly one caller. That asymmetry is the whole
 * safety property of this component, which is why the tests assert the negative
 * as well as the positive.
 *
 * **A failure keeps the dialog open.** Somebody who agreed and hit a 500 must
 * not be returned to a switch that is off with no account of why.
 */
export function ConsentDialog({
  open,
  onAccept,
  onCancel,
  title,
  statement,
  acceptLabel,
  confirmLabel,
  cancelLabel,
  busy = false,
  error,
}: ConsentDialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [accepted, setAccepted] = useState(false);

  useFocusTrap(open, panelRef, onCancel);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div
        data-testid="consent-dialog-overlay"
        onClick={onCancel}
        className="absolute inset-0 backdrop-blur-xl"
        style={{ background: token('bp-scrim') }}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bp-animate-fade-in relative z-10 flex w-full max-w-lg flex-col gap-4 border p-6 outline-none"
        style={{
          background: token('bp-card'),
          borderColor: token('bp-border'),
          // Modals are floating things, so they carry the one shadow the system
          // permits at rest — larger radius than a card's, per the readme.
          boxShadow: token('shadow-xl'),
          borderRadius: token('radius-2xl'),
          color: token('bp-text'),
          animationDuration: motion.normal,
        }}
      >
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>

        {/*
          `whitespace-pre-line` and nothing else. The server's paragraphs are
          part of what was agreed to.
        */}
        <p
          data-testid="consent-statement"
          className="text-sm whitespace-pre-line"
          style={{ color: token('bp-text-secondary') }}
        >
          {statement}
        </p>

        <Checkbox checked={accepted} onChange={setAccepted} label={acceptLabel} showLabel />

        {error === undefined ? null : (
          <p role="alert" className="text-sm" style={{ color: token('bp-danger') }}>
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border px-3 py-1.5 text-sm"
            style={{ borderColor: token('bp-border-strong'), color: token('bp-text-secondary') }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={!accepted || busy}
            aria-busy={busy}
            onClick={() => {
              if (accepted && !busy) onAccept();
            }}
            className="rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: token('bp-blue'), color: token('bp-on-accent') }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
